import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, User, Mail, Lock, Calendar, GraduationCap, Phone, MapPin } from "lucide-react";
import { createApiUrl } from "@/lib/api";

interface StudentRegistrationProps {
  onBack: () => void;
}

const StudentRegistration = ({ onBack }: StudentRegistrationProps) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    date_of_birth: "",
    age: 0,
    alternative_phone: "",
    address: "",
    assigned_teacher_id: "",
    selected_subjects: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTeachers();
    fetchSubjects();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await fetch(createApiUrl('/public/teachers?skip=0&limit=100'), {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeachers(data.teachers || []);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await fetch(createApiUrl('/public/subjects?skip=0&limit=100'), {
        method: 'GET',
        headers: {
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubjects(data.subjects || []);
        setFilteredSubjects(data.subjects || []);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      // Fallback to empty arrays on error
      setSubjects([]);
      setFilteredSubjects([]);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    // Special handling for phone numbers
    if (field === 'phone' || field === 'alternative_phone') {
      // Remove any existing "+91" prefix and non-digit characters
      const cleanNumber = value.toString().replace(/^\+91|[^\d]/g, '');
      
      // Only proceed if we have digits
      if (cleanNumber.length > 0) {
        // Add +91 prefix and limit to 10 digits
        value = `+91${cleanNumber.slice(0, 10)}`;
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    
    // When teacher is selected, show all subjects for student interest selection
    if (field === 'assigned_teacher_id') {
      const selectedTeacher = teachers.find(teacher => teacher.id === value);
      if (selectedTeacher) {
        // If teacher has "General" subject, show all available subjects
        // Otherwise, you could implement specific logic here
        if (selectedTeacher.subject.toLowerCase() === 'general') {
          setFilteredSubjects(subjects); // Show all subjects for General teachers
        } else {
          // For specific subject teachers, still show all subjects for student choice
          // but you could filter if needed
          setFilteredSubjects(subjects);
        }
        // Reset selected subjects when teacher changes
        setFormData(prev => ({ ...prev, selected_subjects: [] }));
      } else {
        setFilteredSubjects([]);
      }
    }
  };

  const handleSubjectSelection = (subjectId: string) => {
    setFormData(prev => {
      const currentSubjects = prev.selected_subjects;
      const isSelected = currentSubjects.includes(subjectId);
      
      if (isSelected) {
        // Remove subject
        return {
          ...prev,
          selected_subjects: currentSubjects.filter(id => id !== subjectId)
        };
      } else {
        // Add subject
        return {
          ...prev,
          selected_subjects: [...currentSubjects, subjectId]
        };
      }
    });
  };

  const handleRegister = async () => {
    if (!formData.name || !formData.phone || !formData.email || !formData.password || !formData.date_of_birth || !formData.age) {
      toast({
        title: "Missing Information",
        description: "Please fill all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Convert date to ISO format for API
      const dateOfBirth = formData.date_of_birth ? new Date(formData.date_of_birth).toISOString() : new Date().toISOString();
      
      const response = await fetch(createApiUrl('/auth/register/student'), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          password: formData.password,
          role: "student",
          age: formData.age,
          assigned_teacher_id: formData.assigned_teacher_id || "default_teacher",
          date_of_birth: dateOfBirth,
          address: formData.address,
          alternative_phone: formData.alternative_phone
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store authentication data using the correct API response structure
        localStorage.setItem('studentData', JSON.stringify(data.user));
        localStorage.setItem('accessToken', data.access_token);

        toast({
          title: "Registration Successful!",
          description: `Welcome to TattvaSphere, ${data.user.name}!`
        });

        // Navigate to student dashboard
        navigate('/student/dashboard');
      } else {
        throw new Error(data.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Registration Failed",
        description: error instanceof Error ? error.message : "Please try again later",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="backdrop-blur-sm bg-white/90 shadow-2xl border-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-pink-50/50 pointer-events-none" />
      <CardHeader className="relative px-4 sm:px-6">
        <div className="flex items-center gap-3 sm:gap-4 mb-4">
          <Button variant="ghost" size="icon" onClick={onBack} className="hover:bg-purple-100 h-8 w-8 sm:h-10 sm:w-10">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
              <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Student Registration
              </CardTitle>
              <p className="text-gray-600 text-sm sm:text-base">Create a new student account</p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative px-4 sm:px-6">
        <div className="space-y-4 sm:space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-700 font-medium">Full Name *</Label>
            <div className="relative">
              <User className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter your full name"
                className="pl-10 h-11 sm:h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500 transition-all duration-200"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
            <div>
              <div className="flex">
                <div className="flex items-center px-3 border border-r-0 rounded-l-md bg-gray-50">
                  +91
                </div>
                <Input
                  id="phone"
                  value={formData.phone.replace(/^\+91/, '')}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Enter 10-digit mobile number"
                  className="rounded-l-none"
                  maxLength={10}
                  pattern="\d{10}"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter your email"
                className="pl-10 h-11 sm:h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500 transition-all duration-200"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700 font-medium">Password *</Label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Create a password"
                className="pl-10 h-11 sm:h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500 transition-all duration-200"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date_of_birth" className="text-gray-700 font-medium">Date of Birth *</Label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  id="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  className="pl-10 h-11 sm:h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500 transition-all duration-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="age" className="text-gray-700 font-medium">Age *</Label>
              <Input
                id="age"
                type="number"
                min="5"
                max="100"
                value={formData.age}
                onChange={(e) => handleInputChange('age', parseInt(e.target.value) || 0)}
                placeholder="Enter your age"
                className="h-11 sm:h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500 transition-all duration-200"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assigned_teacher_id" className="text-gray-700 font-medium">Select Teacher</Label>
              <Select value={formData.assigned_teacher_id} onValueChange={(value) => handleInputChange('assigned_teacher_id', value)}>
                <SelectTrigger className="h-11 sm:h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500">
                  <SelectValue placeholder="Choose your teacher" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map((teacher) => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      👨‍🏫 {teacher.name} - {teacher.subject}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700 font-medium">Select Subjects of Interest</Label>
              <p className="text-xs text-gray-500">Choose subjects you would like to learn (multiple selection allowed)</p>
              {!formData.assigned_teacher_id ? (
                <p className="text-sm text-gray-500 italic">Please select a teacher first</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                  {filteredSubjects.length > 0 ? (
                    filteredSubjects.map((subject) => (
                      <div key={subject.id} className="flex items-center space-x-2 p-2 hover:bg-white rounded transition-colors">
                        <input
                          type="checkbox"
                          id={`subject-${subject.id}`}
                          checked={formData.selected_subjects.includes(subject.id)}
                          onChange={() => handleSubjectSelection(subject.id)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <label 
                          htmlFor={`subject-${subject.id}`}
                          className="text-sm font-medium text-gray-700 cursor-pointer flex-1"
                        >
                          📚 {subject.name}
                          {subject.description && (
                            <span className="text-xs text-gray-500 block mt-1">{subject.description}</span>
                          )}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 italic p-3">Loading subjects...</p>
                  )}
                </div>
              )}
              {formData.selected_subjects.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-600 font-medium">
                    ✓ {formData.selected_subjects.length} subject(s) selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, selected_subjects: [] }))}
                    className="text-xs text-red-500 hover:text-red-700 underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="alternative_phone" className="text-gray-700 font-medium">Alternative Phone (Optional)</Label>
            <div>
              <div className="flex">
                <div className="flex items-center px-3 border border-r-0 rounded-l-md bg-gray-50">
                  +91
                </div>
                <Input
                  id="alternative_phone"
                  value={formData.alternative_phone.replace(/^\+91/, '')}
                  onChange={(e) => handleInputChange('alternative_phone', e.target.value)}
                  placeholder="Enter 10-digit mobile number"
                  className="rounded-l-none"
                  maxLength={10}
                  pattern="\d{10}"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address" className="text-gray-700 font-medium">Address (Optional)</Label>
            <div className="relative">
              <MapPin className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Enter your full address"
                className="pl-10 min-h-[80px] sm:min-h-[100px] border-gray-200 focus:border-purple-500 focus:ring-purple-500 transition-all duration-200 resize-none"
              />
            </div>
          </div>

          <Button 
            onClick={handleRegister} 
            disabled={isLoading}
            className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <GraduationCap className="w-5 h-5 mr-2" />
            {isLoading ? 'Creating Account...' : 'Register Now'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StudentRegistration;
